/**
 * Combined document properties and field mapping table.
 *
 * Displays each document property as a row with its name, value, source,
 * and a dropdown to select a wizard field. Replaces the separate
 * DocumentProperties section and FieldMappingEditor.
 * Uses brand-compliant typography: text-overline on table headers, text-body-sm for body text.
 * Wizard-field selects use the .input-sm compact inset class.
 */

import { useState, useMemo } from 'react';
import { Button } from '../common';
import { WIZARD_FIELD_OPTIONS } from './wizardFieldOptions';
import type { DocumentProperty, FieldMapping } from '../../types/orgTemplate';

interface MappingState {
  wizardField: string;
}

interface PropertyMappingTableProps {
  documentProperties: DocumentProperty[];
  fieldMappings: FieldMapping[];
  onSave: (mappings: FieldMapping[]) => Promise<void>;
  isSaving: boolean;
}

export default function PropertyMappingTable({
  documentProperties,
  fieldMappings,
  onSave,
  isSaving,
}: PropertyMappingTableProps) {
  // Persisted baseline derived from props — the dirty check compares the
  // editable copy against this rather than mirroring it into a second
  // state slot.
  const baseline = useMemo(() => {
    const m: Record<string, MappingState> = {};
    for (const fm of fieldMappings) {
      m[fm.documentProperty] = { wizardField: fm.wizardField };
    }
    return m;
  }, [fieldMappings]);

  const [localMappings, setLocalMappings] =
    useState<Record<string, MappingState>>(baseline);

  // Pattern 0: re-seed the editable copy during render when the persisted
  // mappings change (e.g. after a save + query invalidation). Guard on a
  // stable string key so identity-equal arrays don't re-seed unnecessarily.
  const baselineKey = JSON.stringify(fieldMappings);
  const [prevBaselineKey, setPrevBaselineKey] = useState(baselineKey);
  if (baselineKey !== prevBaselineKey) {
    setPrevBaselineKey(baselineKey);
    setLocalMappings(baseline);
  }

  const isDirty = useMemo(
    () => JSON.stringify(localMappings) !== JSON.stringify(baseline),
    [localMappings, baseline],
  );

  const handleFieldChange = (propertyName: string, wizardField: string) => {
    setLocalMappings((prev) => ({
      ...prev,
      [propertyName]: { wizardField },
    }));
  };

  const handleSave = async () => {
    const mappings: FieldMapping[] = Object.entries(localMappings)
      .filter(([, m]) => m.wizardField)
      .map(([propertyName, m]) => ({
        documentProperty: propertyName,
        wizardField: m.wizardField,
      }));
    await onSave(mappings);
  };

  if (documentProperties.length === 0) {
    return (
      <p className="text-body-sm text-slate-500 dark:text-slate-400">
        No document properties found. Upload a template version to extract properties.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-md border border-slate-200 dark:border-slate-700">
        <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
          <thead className="bg-slate-50 dark:bg-slate-800">
            <tr>
              <th className="px-4 py-2 text-left text-overline uppercase text-slate-500 dark:text-slate-400">Name</th>
              <th className="px-4 py-2 text-left text-overline uppercase text-slate-500 dark:text-slate-400">Value</th>
              <th className="px-4 py-2 text-left text-overline uppercase text-slate-500 dark:text-slate-400">Source</th>
              <th className="px-4 py-2 text-left text-overline uppercase text-slate-500 dark:text-slate-400">Wizard Field</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {documentProperties.map((prop) => {
              const mapping = localMappings[prop.name] || { wizardField: '' };
              return (
                <tr key={`${prop.source}-${prop.name}`}>
                  <td className="px-4 py-2 text-body-sm font-medium text-slate-900 dark:text-slate-100">{prop.name}</td>
                  <td className="px-4 py-2 text-body-sm text-slate-500 dark:text-slate-400">{prop.value}</td>
                  <td className="px-4 py-2 text-body-sm text-slate-500 dark:text-slate-400 capitalize">{prop.source}</td>
                  <td className="px-4 py-2">
                    <select
                      value={mapping.wizardField}
                      onChange={(e) => handleFieldChange(prop.name, e.target.value)}
                      className="input-sm w-full"
                    >
                      {WIZARD_FIELD_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {isDirty && (
        <div className="flex justify-end">
          <Button size="sm" onClick={handleSave} loading={isSaving}>
            Save Mappings
          </Button>
        </div>
      )}
    </div>
  );
}
